import { Cached } from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import {
    Box,
    Button,
    Card,
    CardContent,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Table,
    TableCell,
    TableFooter,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
    styled,
    tableCellClasses,
    tooltipClasses,
    Paper,
    TableBody,
    TableContainer,
    TextField,
} from "@mui/material";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { saveAs } from "file-saver";
import moment from "moment/moment";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ERoles } from "../../config";
import useAuth from "../../hooks/useAuth";
import { apiGatewayAxiosInstance } from "../../utils/axios";
import AlertMessageComponent from "../components/AlertMessageComponent";
import SuccessMessageComponent from "../components/SuccessMessageComponent";
import FilterMISModel from "./filterBillingMisModal";
import { getActiveOrgsForResellerThunk, getMISReportsThunk } from "../../redux/MISReportsManagement/misReportsThunk";
import FetchingSpinner from "../components/FetchingSpinner";
import NoRecords from "../components/NoRecords";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import TablePaginationShowPage from "../../utils/tablePagination";

dayjs.extend(utc);

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        fontSize: 14,
        fontWeight: 600,
        backgroundColor: theme.palette.primary.light,
        color: theme.palette.common.white,
        padding: "10px !important",
        width: "20vw",
    },
    [`&.${tableCellClasses.body}`]: {
        padding: "10px !important",
        fontSize: 14,
    },
    [`&.${tableCellClasses.head.tr}`]: {
        backgroundColor: "#79E0EE",
        width: "45vw",
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    "&:nth-of-type(even)": {
        backgroundColor: theme.palette.action.hover,
    },

    "&:last-child td, &:last-child th": {
        border: 0,
    },
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
    borderCollapse: "separate",
}));

const BootstrapTooltip = styled(({ className, ...props }) => <Tooltip {...props} arrow classes={{ popper: className }} />)(({ theme }) => ({
    [`& .${tooltipClasses.arrow}`]: {
        color: theme.palette.common.black,
        fontSize: 14,
    },
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: theme.palette.common.black,
        fontSize: 14,
    },
}));

const OldBillingMIS = () => {
    const { user } = useAuth();
    const state = useSelector(state => state.misReportsSlice);
    const [selectedClient, setSelectedClient] = useState("");

    const dispatch = useDispatch();

    const [openFilter, setOpenFilter] = useState(false);
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [misData, setMisData] = useState([]);
    const [searchParams, setSearchParams] = useState({
        organizationName: "",
        resellerName: "",
        reportDate: null,
        fromDate: dayjs().startOf("month").format("YYYY-MM-DD").toString(),
        toDate: dayjs().format("YYYY-MM-DD").toString(),
    });
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [operationStatus, setOperationStatus] = useState({
        success: false,
        failed: false,
        message: "",
    });
    const [reload, setReload] = useState(false);
    const [totalMISStats, setTotalMISStats] = useState({
        BASIC_MESSEGE: 0,
        A2P_SINGLE_MESSEGE: 0,
        A2P_CONVERSATION: 0,
        P2A_CONVERSATION: 0,
        SENT_REVOKED: 0,
        TRANS24: 0,
        SMS_DELIVERED: 0,
        SMS_FAILED: 0,
    });
    const [orgNames, setOrgNames] = useState([]);
    const [executedAtStartDate, setExecutedAtStartDate] = useState(dayjs().startOf("month"));
    const [executedAtEndDate, setExecutedAtEndDate] = useState(dayjs());

    const handleClickOpenFilterModel = () => {
        setOpenFilter(true);
    };

    const handleCloseFilterModel = () => {
        setOpenFilter(false);
    };

    const handleChangePage = (event, newPage) => {
        setCurrentPage(newPage);
    };

    const handleChangeRowsPerPage = event => {
        setPageSize(parseInt(event.target.value));
        setCurrentPage(0);
    };

    const handleChangeSearchParams = event => {
        const { name, value } = event.target;
        setSearchParams({ ...searchParams, [name]: value });
    };

    const getMISReports = async newSearchParams => {
        try {
            setLoading(true);
            if (newSearchParams) {
                setSearchParams(newSearchParams);
            }
            dispatch(
                getMISReportsThunk({
                    currentPage: currentPage + 1,
                    pageSize,
                    resellerName: newSearchParams.resellerName,
                    organizationName: newSearchParams.organizationName,
                    fromDate: newSearchParams.fromDate,
                    toDate: newSearchParams.toDate,
                }),
            );
            setLoading(false);
        } catch (error) {
            let errorMessage = "Something Went Wrong!";
            if (error.response) {
                errorMessage = (error.response.data && error.response.data.message) ?? errorMessage;
                dispatch(setOperationStatus({ failed: true, message: errorMessage }));
            } else {
                dispatch(setOperationStatus({ failed: true, message: errorMessage }));
            }
            setLoading(false);
        }
    };

    const handleClose = async ({ fieldName }) => {
        setOperationStatus({
            failed: false,
            success: false,
            message: "",
        });
    };

    const handleUpdateData = async () => {
        setLoading(true);
        try {
            const startDate = moment(new Date(searchParams.fromDate)).format("YYYY-MM-DD");
            const endDate = moment(new Date(searchParams.toDate)).format("YYYY-MM-DD");
            setLoading(true);
            setCurrentPage(0);
            const response = await apiGatewayAxiosInstance.post(`/mis_server/billing/update-billing-mis-reports`, { startDate, endDate });
            setLoading(false);
            setOperationStatus({ success: true, message: response.data });
            reloadList();
        } catch (error) {
            let errorMessage = "Something Went Wrong!";
            if (error.response) {
                errorMessage = (error.response.data && error.response.data.message) ?? errorMessage;
                setOperationStatus({ failed: true, message: errorMessage });
            } else {
                setOperationStatus({ failed: true, message: errorMessage });
            }
        }
        setLoading(false);
    };

    const handleDownloadReport = async () => {
        try {
            setLoading(true);
            const result = await apiGatewayAxiosInstance.post(`/mis_server/billing/download-billing-mis-report`, {
                resellerName: searchParams.resellerName,
                organizationName: searchParams.organizationName,
                fromDate: searchParams.fromDate,
                toDate: searchParams.toDate,
            });
            const downloadData = result.data;
            let includeFields = [];
            let headersMap = {};

            if (user?.roleName.toLowerCase() === ERoles.SUPER_ADMIN) {
                includeFields = [
                    "resellerName",
                    "executedAt",
                    "BASIC_MESSEGE",
                    "A2P_SINGLE_MESSEGE",
                    "A2P_CONVERSATION",
                    "P2A_CONVERSATION",
                    "SENT_REVOKED",
                    "TRANS24",
                    "SMS_DELIVERED",
                    "SMS_FAILED",
                ];
                headersMap = {
                    resellerName: "Reseller Name",
                    executedAt: "Date",
                    BASIC_MESSEGE: "Basic Message",
                    A2P_SINGLE_MESSEGE: "A2P Single Message",
                    A2P_CONVERSATION: "A2P Conversation",
                    P2A_CONVERSATION: "P2A Conversation",
                    SENT_REVOKED: "Sent Revoked",
                    TRANS24: "Trans24",
                    SMS_DELIVERED: "SMS Delivered",
                    SMS_FAILED: "SMS Failed",
                };
            } else if (user?.roleName.toLowerCase() === ERoles.RESELLER_ADMIN) {
                includeFields = [
                    "orgName",
                    "executedAt",
                    "BASIC_MESSEGE",
                    "A2P_SINGLE_MESSEGE",
                    "A2P_CONVERSATION",
                    "P2A_CONVERSATION",
                    "SENT_REVOKED",
                    "TRANS24",
                    "SMS_DELIVERED",
                    "SMS_FAILED",
                ];
                headersMap = {
                    orgName: "Organization Name",
                    executedAt: "Date",
                    BASIC_MESSEGE: "Basic Message",
                    A2P_SINGLE_MESSEGE: "A2P Single Message",
                    A2P_CONVERSATION: "A2P Conversation",
                    P2A_CONVERSATION: "P2A Conversation",
                    SENT_REVOKED: "Sent Revoked",
                    TRANS24: "Trans24",
                    SMS_DELIVERED: "SMS Delivered",
                    SMS_FAILED: "SMS Failed",
                };
            } else if (user?.roleName.toLowerCase() === ERoles.ORG_ADMIN) {
                includeFields = [
                    "executedAt",
                    "BASIC_MESSEGE",
                    "A2P_SINGLE_MESSEGE",
                    "A2P_CONVERSATION",
                    "P2A_CONVERSATION",
                    "SENT_REVOKED",
                    "TRANS24",
                    "SMS_DELIVERED",
                    "SMS_FAILED",
                ];
                headersMap = {
                    executedAt: "Date",
                    BASIC_MESSEGE: "Basic Message",
                    A2P_SINGLE_MESSEGE: "A2P Single Message",
                    A2P_CONVERSATION: "A2P Conversation",
                    P2A_CONVERSATION: "P2A Conversation",
                    SENT_REVOKED: "Sent Revoked",
                    TRANS24: "Trans24",
                    SMS_DELIVERED: "SMS Delivered",
                    SMS_FAILED: "SMS Failed",
                };
            }

            const filteredData = downloadData.map(item => {
                let filteredItem = {};
                includeFields.forEach(key => {
                    if (item.hasOwnProperty(key)) {
                        if (key === "executedAt" && item[key]) {
                            const date = new Date(item[key]);
                            filteredItem[key] = date.toISOString().split("T")[0];
                        } else {
                            filteredItem[key] = item[key];
                        }
                    }
                });
                return filteredItem;
            });

            const convertToCSV = data => {
                const headers = includeFields.map(field => headersMap[field]);
                const array = [headers].concat(data);

                return array.map(row => Object.values(row).join(",")).join("\n");
            };

            const csvData = convertToCSV(filteredData);

            const blob = new Blob([csvData], { type: "text/csv;charset=utf-8;" });
            saveAs(blob, `${moment().format("DD-MM-YYYY")}_Billing_MIS_Report.csv`);
            setOperationStatus({ success: true, message: "Download Successful!" });
            setLoading(false);
        } catch (error) {
            let errorMessage = "Something Went Wrong!";
            if (error.response) {
                errorMessage = (error.response.data && error.response.data.message) ?? errorMessage;
                setOperationStatus({ failed: true, message: errorMessage });
            } else {
                setOperationStatus({ failed: true, message: errorMessage });
            }
            setLoading(false);
        }
    };

    const getTotalMISCounts = misCounts => {
        if (misCounts.length > 0) {
            const totalMISCounts = misCounts.reduce(
                (acc, item) => {
                    acc.BASIC_MESSEGE += item.BASIC_MESSEGE;
                    acc.A2P_SINGLE_MESSEGE += item.A2P_SINGLE_MESSEGE;
                    acc.A2P_CONVERSATION += item.A2P_CONVERSATION;
                    acc.P2A_CONVERSATION += item.P2A_CONVERSATION;
                    acc.SENT_REVOKED += item.SENT_REVOKED;
                    acc.TRANS24 += item.TRANS24;
                    acc.SMS_DELIVERED += item.SMS_DELIVERED;
                    acc.SMS_FAILED += item.SMS_FAILED;
                    return acc;
                },
                {
                    BASIC_MESSEGE: 0,
                    A2P_SINGLE_MESSEGE: 0,
                    A2P_CONVERSATION: 0,
                    P2A_CONVERSATION: 0,
                    SENT_REVOKED: 0,
                    TRANS24: 0,
                    SMS_DELIVERED: 0,
                    SMS_FAILED: 0,
                },
            );
            setTotalMISStats(totalMISCounts);
        }
    };

    const reloadList = () => {
        setReload(!reload);
    };

    const handleClickSearch = async searchParams => {
        setCurrentPage(0);
        setPageSize(10);
        setSearchParams(searchParams);
        const result = await apiGatewayAxiosInstance.get(`/mis_server/billing/get-all-billing-mis-reports`, {
            params: {
                currentPage: 1,
                pageSize: 10,
                resellerName: searchParams.resellerName,
                organizationName: searchParams.organizationName,
                fromDate: searchParams.fromDate,
                toDate: searchParams.toDate,
            },
        });
        setMisData(result.data.misData);
        setTotalCount(result.data.totalCount);
        setLoading(false);
        handleCloseFilterModel();
    };

    // useEffect(() => {
    //     // getMISReports(searchParams);
    //     dispatch(getActiveOrgsForResellerThunk());
    // }, [currentPage, pageSize, reload]);

    const handleClear = () => {
        setSearchParams({
            organizationName: "",
        });
        setExecutedAtStartDate(dayjs().startOf("month"));
        setExecutedAtEndDate(dayjs());
        setReload(!reload);
    };

    useEffect(() => {
        if (user?.roleName.toLowerCase() === ERoles.RESELLER_ADMIN) {
            if (selectedClient) {
                getMISReports(searchParams);
            } else {
                dispatch(getActiveOrgsForResellerThunk());
            }
        }
    }, [currentPage, pageSize, reload, selectedClient]);

    return (
        <>
            <Box>
                <Grid container mb={4} className="listHeader" justifyContent={"space-between"}>
                    <Grid container mb={4} className="listHeader">
                        <Grid item md={6} className="align-items-center">
                            <Typography variant="h3">
                                <b>Billing MIS</b>
                            </Typography>
                        </Grid>

                        <Grid
                            sx={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                            }}
                        >
                            <IconButton
                                title="Refresh"
                                onClick={reloadList}
                                sx={{
                                    animation: loading ? "spin 0.5s linear infinite" : "",
                                    "@keyframes spin": {
                                        "0%": {
                                            transform: "rotate(360deg)",
                                        },
                                        "100%": {
                                            transform: "rotate(0deg)",
                                        },
                                    },
                                }}
                            >
                                <Cached />
                            </IconButton>
                        </Grid>
                    </Grid>
                </Grid>
                <div>
                    <Card className="border border-secondary-50 ">
                        <CardContent
                            sx={{
                                padding: 0,
                            }}
                        >
                            {user?.roleName.toLowerCase() === ERoles.RESELLER_ADMIN && (
                                <Grid padding={2} paddingTop={5}>
                                    <FormControl size="small" fullWidth>
                                        <InputLabel id="client-label">Select Client</InputLabel>
                                        <Select
                                            disabled={state.activeOrgLoading}
                                            name="Active Clients"
                                            labelId="client-label"
                                            id="client-label"
                                            value={selectedClient}
                                            onChange={e => {
                                                setCurrentPage(0);
                                                setPageSize(10);
                                                setSelectedClient(e.target.value);
                                            }}
                                            label="Select Client"
                                            size="small"
                                            props={{
                                                PaperProps: {
                                                    sx: {
                                                        boxShadow: " 0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)",
                                                    },
                                                },
                                            }}
                                        >
                                            {state.activeOrgList &&
                                                state.activeOrgList?.length > 0 &&
                                                state.activeOrgList?.map((org, index) => (
                                                    <MenuItem key={index} value={org._id}>
                                                        <Grid
                                                            container
                                                            sx={{
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "space-between",
                                                            }}
                                                        >
                                                            <Grid item md={6} display={"flex"} flexDirection={"column"}>
                                                                <Typography sx={{ fontSize: "0.9rem", fontWeight: "600" }}>{org?.orgName}</Typography>
                                                            </Grid>
                                                        </Grid>
                                                    </MenuItem>
                                                ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            )}

                            {user?.roleName.toLowerCase() === ERoles.RESELLER_ADMIN && !selectedClient ? (
                                <Typography color="black" fontWeight={500} textAlign={"center"}>
                                    Select Client
                                </Typography>
                            ) : (
                                <>
                                    <Grid
                                        sx={{
                                            // display: "flex",
                                            // padding: 3,
                                            display: "flex",
                                            justifyContent: "space-between",
                                            flexDirection: "row",
                                            gap: 2,
                                            padding: 5,
                                        }}
                                    >
                                        <Grid
                                            sx={{
                                                display: "flex",
                                                flexDirection: "row",
                                                gap: 5,
                                            }}
                                        >
                                            <Grid>
                                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                    <DatePicker
                                                        label="Start Date"
                                                        value={executedAtStartDate}
                                                        onChange={value => setExecutedAtStartDate(dayjs(value))}
                                                        format="DD-MM-YYYY"
                                                        name="startDate"
                                                        disableFuture
                                                        sx={{
                                                            "& .MuiInputBase-root": {
                                                                height: "36px",
                                                                padding: "0 10px",
                                                            },
                                                        }}
                                                    />
                                                </LocalizationProvider>
                                            </Grid>
                                            <Grid>
                                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                    <DatePicker
                                                        label="End Date"
                                                        value={executedAtEndDate}
                                                        onChange={value => setExecutedAtEndDate(dayjs(value))}
                                                        format="DD-MM-YYYY"
                                                        name="startDate"
                                                        disableFuture
                                                        sx={{
                                                            "& .MuiInputBase-root": {
                                                                height: "36px",
                                                                padding: "0 10px",
                                                            },
                                                        }}
                                                    />
                                                </LocalizationProvider>
                                            </Grid>
                                        </Grid>
                                        <Grid
                                            sx={{
                                                display: "flex",
                                                justifyContent: "center",
                                                alignItems: "center",
                                                gap: 2,
                                            }}
                                        >
                                            <Button
                                                onClick={handleClear}
                                                color="error"
                                                variant="contained"
                                                sx={{
                                                    display: "flex",
                                                    justifyContent: "center",
                                                    alignItems: "center",
                                                    gap: 1,
                                                    padding: "6px 16px",
                                                }}
                                            >
                                                Clear
                                            </Button>
                                            <Button
                                                color="primary"
                                                variant="contained"
                                                sx={{
                                                    display: "flex",
                                                    justifyContent: "center",
                                                    alignItems: "center",
                                                    gap: 1,
                                                    padding: "6px 16px",
                                                }}
                                                onClick={() => setReload(!reload)}
                                            >
                                                Search
                                            </Button>
                                        </Grid>
                                    </Grid>

                                    {state.getBillingMISReportsLoading ? (
                                        <FetchingSpinner />
                                    ) : state.billingMISReports && state?.billingMISReports?.length > 0 ? (
                                        <StyledTableContainer component={Paper} className="table-container" sx={{ maxHeight: "70vh" }}>
                                            <Table className="center" stickyHeader>
                                                <TableHead className="p-3 mb-2 row">
                                                    <TableRow>
                                                        <StyledTableCell align="left" className="col-1 tableHeaderFont">
                                                            <Typography fontSize={14}>Sr No</Typography>
                                                        </StyledTableCell>
                                                        {!(user?.roleName.toLowerCase() === ERoles.ORG_ADMIN) && (
                                                            <StyledTableCell align="left" className="col-1 tableHeaderFont">
                                                                <Typography fontSize={14}>
                                                                    {user?.roleName.toLowerCase() === ERoles.RESELLER_ADMIN && "Organization Name"}
                                                                    {user?.roleName.toLowerCase() === ERoles.SUPER_ADMIN && "Reseller Name"}
                                                                </Typography>
                                                            </StyledTableCell>
                                                        )}
                                                        <StyledTableCell align="center" className="col-1 tableHeaderFont">
                                                            <Typography fontSize={14}>Date</Typography>
                                                        </StyledTableCell>
                                                        <StyledTableCell align="center" className="col-1 tableHeaderFont">
                                                            <Typography fontSize={14}>Basic Message</Typography>
                                                        </StyledTableCell>
                                                        <StyledTableCell align="center" className="col-1 tableHeaderFont">
                                                            <Typography fontSize={14}>A2P Single Messege</Typography>
                                                        </StyledTableCell>
                                                        <StyledTableCell align="center" className="col-1 tableHeaderFont">
                                                            <Typography fontSize={14}>A2P Conversation</Typography>
                                                        </StyledTableCell>
                                                        <StyledTableCell align="center" className="col-1 tableHeaderFont">
                                                            <Typography fontSize={14}>P2A Conversation</Typography>
                                                        </StyledTableCell>
                                                        <StyledTableCell align="center" className="col-1 tableHeaderFont">
                                                            <Typography fontSize={14}>Sent Revoked</Typography>
                                                        </StyledTableCell>
                                                        <StyledTableCell align="center" className="col-1 tableHeaderFont">
                                                            <Typography fontSize={14}>Trans 24</Typography>
                                                        </StyledTableCell>
                                                        <StyledTableCell align="center" className="col-1 tableHeaderFont">
                                                            <Typography fontSize={14}>SMS Delivered</Typography>
                                                        </StyledTableCell>
                                                        <StyledTableCell align="center" className="col-1 tableHeaderFont">
                                                            <Typography fontSize={14}>SMS Failed</Typography>
                                                        </StyledTableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {state?.billingMISReports &&
                                                        state?.billingMISReports.map((data, index) => (
                                                            <StyledTableRow key={data.id || index}>
                                                                <StyledTableCell align="center">{currentPage * pageSize + index + 1}</StyledTableCell>
                                                                {!(user?.roleName.toLowerCase() === ERoles.ORG_ADMIN) && (
                                                                    <StyledTableCell align="center">
                                                                        <Typography fontSize={14}>
                                                                            {user?.roleName.toLowerCase() === ERoles.RESELLER_ADMIN && data.orgName}
                                                                            {user?.roleName.toLowerCase() === ERoles.SUPER_ADMIN && data.resellerName}
                                                                        </Typography>
                                                                    </StyledTableCell>
                                                                )}
                                                                <StyledTableCell align="center">
                                                                    <Typography className="tableContentFontForDateAndTime">
                                                                        {new Date(data.executedAt).toLocaleDateString([], { day: "2-digit" })}-
                                                                        {new Date(data.executedAt).toLocaleDateString([], { month: "2-digit" })}-
                                                                        {new Date(data.executedAt).toLocaleDateString([], { year: "numeric" })}
                                                                    </Typography>
                                                                </StyledTableCell>
                                                                <StyledTableCell align="center">{data.BASIC_MESSEGE}</StyledTableCell>
                                                                <StyledTableCell align="center">{data.A2P_SINGLE_MESSEGE}</StyledTableCell>
                                                                <StyledTableCell align="center">{data.A2P_CONVERSATION}</StyledTableCell>
                                                                <StyledTableCell align="center">{data.P2A_CONVERSATION}</StyledTableCell>
                                                                <StyledTableCell align="center">{data.SENT_REVOKED}</StyledTableCell>
                                                                <StyledTableCell align="center">{data.TRANS24}</StyledTableCell>
                                                                <StyledTableCell align="center">{data.SMS_DELIVERED}</StyledTableCell>
                                                                <StyledTableCell align="center">{data.SMS_FAILED}</StyledTableCell>
                                                            </StyledTableRow>
                                                        ))}
                                                </TableBody>
                                                <TableFooter
                                                    className="sticky-footer"
                                                    sx={{
                                                        position: "sticky",
                                                        bottom: 0,
                                                        backgroundColor: "white",
                                                        zIndex: 1,
                                                    }}
                                                >
                                                    <StyledTableRow>
                                                        <StyledTableCell className="tableHeaderFont" align="center">
                                                            {"Total: "}
                                                        </StyledTableCell>
                                                        {!(user?.roleName.toLowerCase() === ERoles.ORG_ADMIN) && (
                                                            <StyledTableCell className="tableHeaderFont" align="center"></StyledTableCell>
                                                        )}
                                                        <StyledTableCell className="tableHeaderFont" align="center"></StyledTableCell>
                                                        <StyledTableCell className="tableHeaderFont" align="center">
                                                            {totalMISStats.BASIC_MESSEGE}
                                                        </StyledTableCell>
                                                        <StyledTableCell className="tableHeaderFont" align="center">
                                                            {totalMISStats.A2P_SINGLE_MESSEGE}
                                                        </StyledTableCell>
                                                        <StyledTableCell className="tableHeaderFont" align="center">
                                                            {totalMISStats.A2P_CONVERSATION}
                                                        </StyledTableCell>
                                                        <StyledTableCell className="tableHeaderFont" align="center">
                                                            {totalMISStats.P2A_CONVERSATION}
                                                        </StyledTableCell>
                                                        <StyledTableCell className="tableHeaderFont" align="center">
                                                            {totalMISStats.SENT_REVOKED}
                                                        </StyledTableCell>
                                                        <StyledTableCell className="tableHeaderFont" align="center">
                                                            {totalMISStats.TRANS24}
                                                        </StyledTableCell>
                                                        <StyledTableCell className="tableHeaderFont" align="center">
                                                            {totalMISStats.SMS_DELIVERED}
                                                        </StyledTableCell>
                                                        <StyledTableCell className="tableHeaderFont" align="center">
                                                            {totalMISStats.SMS_FAILED}
                                                        </StyledTableCell>
                                                    </StyledTableRow>
                                                </TableFooter>
                                            </Table>
                                        </StyledTableContainer>
                                    ) : (
                                        <NoRecords />
                                    )}
                                </>
                            )}
                        </CardContent>
                        {openFilter && (
                            <FilterMISModel
                                open={openFilter}
                                searchParams={searchParams}
                                handleCloseFilterModel={handleCloseFilterModel}
                                getMISReports={getMISReports}
                                handleClickSearch={handleClickSearch}
                            />
                        )}
                        {operationStatus.failed && (
                            <AlertMessageComponent
                                message={operationStatus.message}
                                fieldName={operationStatus.message}
                                handleClose={handleClose}
                                show={operationStatus.exist}
                            />
                        )}
                        {operationStatus.success && (
                            <SuccessMessageComponent
                                message={operationStatus.message}
                                fieldName={operationStatus.message}
                                handleClose={handleClose}
                                show={operationStatus.success}
                            />
                        )}
                    </Card>
                </div>
            </Box>
        </>
    );
};

export default OldBillingMIS;
